/* Created by Interactive 63 - https://interactive63.com */
/**
 * Stroke reveal controls: start and end percentages with a preview bar of the visible portion,
 * and a "Draw on" shortcut that keys the stroke drawing in over a second.
 */
import type { DrawValue } from '../../../../core/model/types';
import { h } from '../../../ui/dom';
import { NumberInput } from './number-input';

export interface DrawInputOptions {
  scrubKey?: string;
  onChange: (value: Partial<DrawValue>, coalesceKey?: string) => void;
  onDrawOn: () => void;
}

export class DrawInput {
  readonly el: HTMLElement;
  private readonly start: NumberInput;
  private readonly end: NumberInput;
  private readonly fill: HTMLElement;
  private readonly drawOn: HTMLButtonElement;

  /** Builds the fields, preview bar and button. */
  constructor(options: DrawInputOptions) {
    const shared = { unit: '%', min: 0, max: 100, step: 0.1, scrubKey: options.scrubKey };
    this.start = new NumberInput({
      ...shared,
      label: 'Start',
      onChange: (start, key) => {
        options.onChange({ start }, key);
      },
    });
    this.end = new NumberInput({
      ...shared,
      label: 'End',
      onChange: (end, key) => {
        options.onChange({ end }, key);
      },
    });
    this.fill = h('span', { class: 'draw-fill' });
    this.drawOn = h(
      'button',
      { type: 'button', class: 'draw-on', title: 'Key the stroke reveal on over one second' },
      'Draw on',
    );
    this.drawOn.addEventListener('click', options.onDrawOn);
    this.el = h(
      'span',
      { class: 'draw-input' },
      h('span', { class: 'draw-fields' }, this.start.el, this.end.el),
      h('span', { class: 'draw-bar', 'aria-hidden': 'true' }, this.fill),
      this.drawOn,
    );
  }

  /** Shows values; null components display as "Mixed". */
  setValue(value: { start: number | null; end: number | null }, force = false): void {
    this.start.setValue(value.start, force);
    this.end.setValue(value.end, force);
    const start = value.start ?? 0;
    const end = value.end ?? 100;
    this.fill.style.left = `${Math.max(0, Math.min(100, start))}%`;
    this.fill.style.width = `${Math.max(0, Math.min(100, end) - Math.max(0, start))}%`;
  }

  /** Makes the controls read-only. */
  setReadOnly(readOnly: boolean): void {
    this.start.setReadOnly(readOnly);
    this.end.setReadOnly(readOnly);
    this.drawOn.disabled = readOnly;
  }
}
